<template>
  <div class="survey-info-widget card h-100">
    <WidgetHeader
      :title="widgetTitle"
      :swiperIsActive="!swiperHasReachedEnd"
      :onBack="state !== 'default' ? back : null"
    />
    <div
      v-if="trendTags.participation && debugParticipationTrend"
      class="alert alert-info"
    >
      <h2>{{ $t('widgetSurveyInfo.participation.title') }}</h2>
      <table class="table">
        <tbody>
          <tr>
            <td
              v-for="a in trendTags.participation.averages"
              :key="a.surveyName"
            >
              <h4>
                {{ a.value }}
              </h4>
              <span>{{ a.surveyName }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <WidgetBody
      :id="id"
      :onSwiperInitCallback="onSwiperInit"
      :initialSlideIndex="widgetSliderState.index"
      :updateSliderIndex="updateSliderIndex"
      :viewMode="viewMode"
      @next-transition-start="updateSliderIndex"
      @prev-transition-start="updateSliderIndex"
    >
      <template #slide-0>
        <SurveyInfoDates :id="id" :startDate="startDate" :endDate="endDate" />
        <SurveyInfoParticipation
          v-if="participationTag && shouldShowParticipation"
          :id="id"
          :participationSettingsForGroup="participationSettingsForGroup"
          :participationTag="participationTag"
          :onClickParticipationTag="
            hasHistoricalData ? onClickParticipationTagCallback : null
          "
          :selectedAttributeSegment="selectedAttributeSegment"
          :containsOnlyFreeTextQuestions="surveyContainsOnlyFreeTextQuestions"
        />
        <SurveyInfoStatus
          :surveyDetails="surveyDetails"
          :hasResults="hasResults"
          :containsOnlyFreeTextQuestions="surveyContainsOnlyFreeTextQuestions"
        />
      </template>
      <template #slide-1>
        <TagTrendsDetails
          :id="id"
          :tags="{ participation: participationTag }"
          :trendTags="trendTags"
          :zeroToHundred="true"
          :selectedAttributeSegment="selectedAttributeSegment"
        />

        <div v-if="comparedToBenchmarkText" class="pt-10 mt-10">
          <span>
            {{ comparedToBenchmarkText }}
          </span>
        </div>
      </template>
    </WidgetBody>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { requestTypes } from '@/constants/request-types'
import commonWidgetProps from '@/components/mixins/common-widget-props'
import SurveyInfoDates from './subcomponents/SurveyInfoDates.vue'
import SurveyInfoStatus from './subcomponents/SurveyInfoStatus.vue'
import SurveyInfoParticipation from './subcomponents/SurveyInfoParticipation.vue'
import TagTrendsDetails from '@/components/shared/TagTrendsDetails.vue'
import WidgetHeader from '@/components/widgets/shared/WidgetHeader.vue'
import WidgetBody from '@/components/widgets/shared/WidgetBody.vue'
import { getComparedToBenchmarkText } from '@/helpers/results-helper'

export const surveyInfoWidgetInformation = () => {
  return {
    requests: {
      [requestTypes.SURVEY_DETAILS]: {},
      [requestTypes.TAGS]: {
        filter: { participation: {} }
      },
      [requestTypes.TREND_TAGS]: {
        participation: {}
      }
    }
  }
}

export const surveyInfoWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: [{ height: 8 }, { height: 8 }]
      },
      {
        cols: [{ height: 6 }, { height: 6 }]
      },
      {
        cols: [{ height: 4 }]
      }
    ]
  }
}

const SurveyInfoWidget = {
  name: 'SurveyInfoWidget',
  components: {
    TagTrendsDetails,
    SurveyInfoDates,
    SurveyInfoStatus,
    SurveyInfoParticipation,
    WidgetHeader,
    WidgetBody
  },
  mixins: [commonWidgetProps],
  props: {
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    },
    trendTags: {
      type: Object,
      default: () => {}
    },
    surveyContainsOnlyFreeTextQuestions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      state: 'default',
      debugParticipationTrend: false,
      swiperInstance: null,
      swiperHasReachedEnd: false
    }
  },
  mounted() {
    if (this.widgetSliderState.index > 0) {
      if (this.hasHistoricalData) {
        this.state = 'participation'
      } else {
        this.swiperInstance.slidePrev()
        this.state = 'default'
      }
    }
  },
  computed: {
    hasHistoricalData() {
      return this.trendTags.participation?.averages?.length > 1
    },
    participationSettingsForGroup() {
      if (this.selectedGroup.name === this.organizationName) {
        return this.surveyDetails.details.resultViews.participation || {}
      } else if (this.selectedGroup.config?.results?.participation) {
        return this.selectedGroup.config.results.participation
      } else {
        return this.surveyDetails.details.resultViews.participation || {}
      }
    },
    participationTag() {
      const messagesWithFallbackTitle = Object.entries(
        this.tags.participation?.messages || {}
      ).reduce((messages, [locale, value]) => {
        messages[locale] = value
          ? { ...value, title: value.title }
          : { title: this.$t('widgetSurveyInfo.participation.title') }
        return messages
      }, {})
      return { ...this.tags.participation, messages: messagesWithFallbackTitle }
    },
    startDate() {
      return DateTime.fromISO(
        this.surveyDetails.details.start_date
      ).toLocaleString(DateTime.DATE_HUGE)
    },
    endDate() {
      return DateTime.fromISO(
        this.surveyDetails.details.end_date
      ).toLocaleString(DateTime.DATE_HUGE)
    },
    shouldShowParticipation() {
      // either looking at all segment or participation settings does not tell us to hide respondeed
      return (
        this.selectedAttributeSegment.name === 'all' ||
        !this.participationSettingsForGroup.hideResponded
      )
    },
    widgetTitle() {
      if (this.state === 'participation') {
        return this.$tr(this.participationTag.messages, 'title')
      } else {
        return ''
      }
    },
    comparedToBenchmarkText() {
      return getComparedToBenchmarkText({
        t: this.$t,
        tr: this.$tr,
        localeNumber: this.$localeNumber,
        item: {
          name: 'participation',
          ...this.participationTag,
          value: this.participationTag.value * 10
        },
        shouldBeZeroToHundred: true
      })
    }
  },
  methods: {
    back() {
      this.swiperInstance.slidePrev()
      this.state = 'default'
    },
    onClickParticipationTagCallback() {
      this.swiperInstance.slideNext()
      this.state = 'participation'
    },
    onSwiperInit(swiper) {
      this.swiperInstance = swiper
      this.swiperInstance.on('slideChangeTransitionEnd', () => {
        if (swiper.activeIndex === 1) {
          this.swiperHasReachedEnd = true
        } else {
          this.swiperHasReachedEnd = false
        }
      })
    },
    updateSliderIndex(swiper) {
      this.updateWidgetSliderState({
        id: this.id,
        index: swiper.activeIndex,
        info: { currentCategory: this.currentCategory }
      })
    }
  }
}

export default SurveyInfoWidget
</script>

<style></style>
